import { CompanyType, type UserFeatureFlag, UserProfileRole, UserStatus } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { BadgeList } from '@components/shared/badge-list.component';
import { UserRoleBadge } from '@components/shared/user-role-badge.component';
import { UserStatusBadge } from '@components/shared/user-status-badge.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Calendar } from '@components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Skeleton } from '@components/ui/skeleton';
import { Textarea } from '@components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { errorToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import { cn } from '@lib/utils';
import { useAdminImpersonateUser, useAdminUserDetailQuery } from '@queries/admin-users.query';
import { useCurrentUserQueryOptions } from '@queries/users.query';
import { routes } from '@routes';
import { useQueryClient } from '@tanstack/react-query';
import { safeFormatDateTime } from '@utils/date.util';
import { shouldRedirectAuthenticatedUserToAdmin } from '@utils/impersonation-session.util';
import { getIntelligencePersonaBadgeVariant, getIntelligencePersonaLabel } from '@utils/intelligence-persona.util';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  CopyIcon,
  MailIcon,
  ShieldAlertIcon,
  ShieldBanIcon,
  UserIcon,
  XCircleIcon,
} from 'lucide-react';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminUsersModalFeatureFlags } from './admin-users-modal-feature-flags.component';
import { AdminUsersModalTags } from './admin-users-modal-tags.component';

type AdminUsersModalProps = {
  isBusy: boolean;
  isOpen: boolean;
  onApproveToggle: (userId: string, nextValue: boolean) => void;
  onBlockToggle: (userId: string, shouldBlock: boolean, reason?: string) => void;
  onClose: () => void;
  onMarkAsVerified: (userId: string) => void;
  onResendVerification: (userId: string) => void;
  onSetAccessWindow: (userId: string, accessExpiredAt: string | null) => void;
  onUpdateUserFeatureFlags: (userId: string, featureFlags: UserFeatureFlag[]) => void;
  userId?: string;
};

type AdminUsersModalContentProps = {
  isBusy: boolean;
  onApproveToggle: (userId: string, nextValue: boolean) => void;
  onBlockToggle: (userId: string, shouldBlock: boolean, reason?: string) => void;
  onMarkAsVerified: (userId: string) => void;
  onResendVerification: (userId: string) => void;
  onSetAccessWindow: (userId: string, accessExpiredAt: string | null) => void;
  onUpdateUserFeatureFlags: (userId: string, featureFlags: UserFeatureFlag[]) => void;
  userId: string;
};

const AdminUsersModalContent: React.FC<AdminUsersModalContentProps> = (props) => {
  const {
    isBusy,
    onApproveToggle,
    onBlockToggle,
    onMarkAsVerified,
    onResendVerification,
    onSetAccessWindow,
    onUpdateUserFeatureFlags,
    userId,
  } = props;
  const { data: user } = useAdminUserDetailQuery(userId);

  // State for date picker dialog
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    user.accessExpiredAt ? new Date(user.accessExpiredAt) : undefined,
  );

  // State for block reason popover
  const [isBlockPopoverOpen, setIsBlockPopoverOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  // State for Intelligence user approval confirmation
  const [showIntelligenceConfirmation, setShowIntelligenceConfirmation] = useState(false);

  // Navigation for category modal
  const navigate = useNavigate();

  // Update selected date when user data changes
  useEffect(() => {
    setSelectedDate(user.accessExpiredAt ? new Date(user.accessExpiredAt) : undefined);
  }, [user.accessExpiredAt]);

  // Copy handling - using the DRY hook
  const { handleCopy, isCopied } = useClipboardCopy();

  const isExpired = useMemo(() => {
    if (!user?.accessExpiredAt || typeof user.accessExpiredAt !== 'string') return false;

    try {
      return new Date(user.accessExpiredAt).getTime() <= Date.now();
    } catch {
      return false;
    }
  }, [user?.accessExpiredAt]);

  const handleApprovalClick = useCallback(() => {
    const isApproving = user.status !== UserStatus.APPROVED;
    const isIntelligence = user.profile?.role === UserProfileRole.INTELLIGENCE;

    // If approving an Intelligence user, show confirmation modal
    if (isApproving && isIntelligence) {
      setShowIntelligenceConfirmation(true);
      return;
    }

    // Otherwise, proceed with normal approval/unapproval
    onApproveToggle(userId, isApproving);
  }, [user.status, user.profile?.role, userId, onApproveToggle]);

  const handleConfirmIntelligenceApproval = useCallback(() => {
    onApproveToggle(userId, true);
    setShowIntelligenceConfirmation(false);
  }, [userId, onApproveToggle]);

  const userRole = user.profile?.role;
  const userName = user.profile?.fullName || user.email;
  const hasUserCompanies = !!user.userCompanies && user.userCompanies.length > 0;
  const organizationCount = user.userCompanies?.length ?? 0;
  const organizationTitle = organizationCount === 1 ? 'Organization' : 'Organizations';

  return (
    <div className="flex flex-col gap-4">
      {/* Main Content Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column - Details */}
        <div className="flex flex-col gap-4">
          <div className="group relative p-4 rounded-lg border border-gray-200 bg-white">
            <IconButton
              ariaLabel="Copy Details"
              className="absolute top-2 right-2 border border-gray-200 hidden group-hover:flex"
              icon={isCopied('Details') ? <CheckIcon /> : <CopyIcon />}
              onClick={() =>
                handleCopy('Details', {
                  id: userId,
                  name: userName,
                  email: user.email,
                  role: userRole,
                  title: user.profile?.title,
                  joined: safeFormatDateTime(user.createdAt),
                })
              }
              size="sm"
              variant="secondary"
            />
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Details</h3>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">ID</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 font-mono text-xs truncate">{userId}</span>
                  <IconButton
                    ariaLabel="Copy ID"
                    className="border border-gray-200 shrink-0"
                    icon={isCopied('ID') ? <CheckIcon /> : <CopyIcon />}
                    onClick={() => handleCopy('ID', userId)}
                    size="sm"
                    variant="secondary"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Name</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{userName}</span>
                  <IconButton
                    ariaLabel="Copy Name"
                    className="border border-gray-200 shrink-0"
                    icon={isCopied('Name') ? <CheckIcon /> : <CopyIcon />}
                    onClick={() => handleCopy('Name', userName)}
                    size="sm"
                    variant="secondary"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Email</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{user.email}</span>
                  <IconButton
                    ariaLabel="Copy Email"
                    className="border border-gray-200 shrink-0"
                    icon={isCopied('Email Details') ? <CheckIcon /> : <CopyIcon />}
                    onClick={() => handleCopy('Email Details', user.email)}
                    size="sm"
                    variant="secondary"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Role</span>
                <div className="flex items-center gap-2">
                  {userRole ? <UserRoleBadge role={userRole} /> : <span className="text-sm text-gray-900">—</span>}
                </div>
              </div>

              {user.profile?.title && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Title</span>
                  <span className="text-sm text-gray-900">{user.profile.title}</span>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Joined</span>
                <span className="text-sm text-gray-900">{safeFormatDateTime(user.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Skills Section */}
          {(user.profile?.jobRole ||
            user.profile?.category ||
            user.profile?.subcategory ||
            (user.profile?.researchKeywords && user.profile.researchKeywords.length > 0)) && (
            <div className="group relative p-4 rounded-lg border border-gray-200 bg-white">
              <IconButton
                ariaLabel="Copy Skills"
                className="absolute top-2 right-2 border border-gray-200 hidden group-hover:flex"
                icon={isCopied('Skills') ? <CheckIcon /> : <CopyIcon />}
                onClick={() =>
                  handleCopy('Skills', {
                    jobRole: user.profile?.jobRole?.name,
                    category: user.profile?.category?.name,
                    subcategory: user.profile?.subcategory?.name,
                    researchKeywords: user.profile?.researchKeywords?.map((k) => k.name),
                  })
                }
                size="sm"
                variant="secondary"
              />
              <div className="flex flex-col gap-3">
                <h3 className="text-sm font-semibold text-gray-900">Skills</h3>

                {user.profile?.jobRole && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">Job Role</span>
                    <span className="text-sm text-gray-900">{user.profile.jobRole.name}</span>
                  </div>
                )}

                {user.profile?.category && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">Category</span>
                    <Button
                      onClick={() => navigate(`/admin/categories?categoryId=${user.profile!.category!.id}`)}
                      variant="link"
                      className="sm:w-auto justify-start"
                    >
                      {user.profile.category.name}
                    </Button>
                  </div>
                )}

                {user.profile?.subcategory && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-500">Subcategory</span>
                    <Button
                      onClick={() => navigate(`/admin/categories?categoryId=${user.profile!.subcategory!.id}`)}
                      variant="link"
                      className="sm:w-auto justify-start"
                    >
                      {user.profile.subcategory.name}
                    </Button>
                  </div>
                )}

                {user.profile?.researchKeywords && user.profile.researchKeywords.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-medium text-gray-500">Research Keywords</span>
                    <BadgeList
                      items={user.profile.researchKeywords.map((k) => ({ id: k.id, name: k.name }))}
                      badgeVariant="secondary"
                      size="sm"
                      limit={5}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Organization Section */}
          {hasUserCompanies && (
            <div className="p-4 border rounded-lg border-gray-200">
              <div className="flex flex-col gap-3">
                <h3 className="text-sm font-semibold text-gray-900">{organizationTitle}</h3>

                {user.userCompanies?.map((userCompany, index) => (
                  <div
                    key={userCompany.company?.id || index}
                    className={cn(
                      'group/org flex items-center justify-between gap-2 rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50',
                      index < (user.userCompanies?.length ?? 0) - 1 && 'mb-2',
                    )}
                  >
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col gap-0.5 min-w-0 flex-1 cursor-default">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {userCompany.company?.name || '—'}
                            </span>
                            {userCompany.company?.type === CompanyType.INTELLIGENCE ? (
                              <div className="flex">
                                <Badge
                                  size="xs"
                                  variant={getIntelligencePersonaBadgeVariant(userCompany.persona)}
                                >
                                  {getIntelligencePersonaLabel(userCompany.persona)}
                                </Badge>
                              </div>
                            ) : (
                              userCompany.role && <span className="text-xs text-gray-500">{userCompany.role}</span>
                            )}
                          </div>
                        </TooltipTrigger>
                        {userCompany.company?.id && (
                          <TooltipContent className="font-mono text-xs">{userCompany.company.id}</TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>

                    {userCompany.company?.id && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <IconButton
                              ariaLabel="View organization"
                              className="border border-gray-200 shrink-0 opacity-0 group-hover/org:opacity-100 transition-opacity pointer-events-none group-hover/org:pointer-events-auto"
                              icon={<ArrowRightIcon className="size-3" />}
                              onClick={() => navigate(`/admin/organizations?orgId=${userCompany.company?.id}`)}
                              size="sm"
                              variant="secondary"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="font-normal text-xs">View in Admin Organizations</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Status */}
        <div className="flex flex-col gap-4">
          <div className="group relative p-4 rounded-lg border border-gray-200 bg-white">
            <IconButton
              ariaLabel="Copy Status"
              className="absolute top-2 right-2 border border-gray-200 hidden group-hover:flex"
              icon={isCopied('Status') ? <CheckIcon /> : <CopyIcon />}
              onClick={() =>
                handleCopy('Status', {
                  status: user.status,
                  approved: user.approved,
                  emailVerified: user.emailVerified,
                  accessExpires: safeFormatDateTime(user.accessExpiredAt),
                })
              }
              size="sm"
              variant="secondary"
            />
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Status</h3>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Status</span>
                <div className="flex items-center gap-2">
                  <UserStatusBadge
                    size="sm"
                    status={user.status}
                  />
                  <div className="ml-auto flex gap-1">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className="border border-gray-200 shrink-0"
                            onClick={handleApprovalClick}
                            size="xs"
                            variant="secondary"
                            disabled={
                              isBusy ||
                              user.status === UserStatus.BLOCKED ||
                              (!user.emailVerified && user.status !== UserStatus.APPROVED)
                            }
                          >
                            {user.status === UserStatus.APPROVED ? (
                              <XCircleIcon className="size-3" />
                            ) : (
                              <CheckCircleIcon className="size-3" />
                            )}
                            <span>{user.status === UserStatus.APPROVED ? 'Unapprove' : 'Approve'}</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="font-normal text-xs">
                          <p>
                            {user.status === UserStatus.BLOCKED
                              ? 'Cannot approve blocked users. Unblock the user first.'
                              : !user.emailVerified && user.status !== UserStatus.APPROVED
                                ? 'User must verify their email before approval'
                                : user.status === UserStatus.APPROVED
                                  ? 'Revoke user approval and set to pending'
                                  : 'Approve user and activate account'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {user.status === UserStatus.BLOCKED ? (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              className="border border-gray-200 shrink-0"
                              onClick={() => onBlockToggle(userId, false)}
                              size="xs"
                              variant="secondary"
                              disabled={isBusy}
                            >
                              <ShieldAlertIcon className="size-3" />
                              <span>Unblock</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="font-normal text-xs">
                            <p>Unblock user and restore access</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Popover
                        open={isBlockPopoverOpen}
                        onOpenChange={setIsBlockPopoverOpen}
                      >
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <PopoverTrigger asChild>
                                <Button
                                  className="border border-red-200 shrink-0"
                                  size="xs"
                                  variant="destructive"
                                  disabled={isBusy}
                                >
                                  <ShieldBanIcon className="size-3" />
                                  <span>Block</span>
                                </Button>
                              </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent className="font-normal text-xs">
                              <p>Block user from accessing the platform</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <PopoverContent
                          align="start"
                          className="w-80 p-3"
                          side="right"
                        >
                          <div className="flex flex-col gap-3">
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">Block User</h4>
                              <p className="text-xs text-gray-600 mt-1">
                                Optionally provide a reason for blocking this user.
                              </p>
                            </div>
                            <div className="relative">
                              <Textarea
                                className="resize-none text-sm"
                                disabled={isBusy}
                                maxLength={100}
                                onChange={(e) => setBlockReason(e.target.value)}
                                placeholder="(Optional) Reason for blocking"
                                rows={3}
                                value={blockReason}
                              />
                              <div className="flex justify-end mt-1">
                                <span className="text-xs text-gray-500">{blockReason.length}/100</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                onClick={() => {
                                  setIsBlockPopoverOpen(false);
                                  setBlockReason('');
                                }}
                                size="xs"
                                variant="secondary"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={() => {
                                  onBlockToggle(userId, true, blockReason.trim() || undefined);
                                  setIsBlockPopoverOpen(false);
                                  setBlockReason('');
                                }}
                                size="xs"
                                variant="destructive"
                                disabled={isBusy}
                              >
                                <ShieldBanIcon className="size-3 mr-1" />
                                Block User
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </div>

              {user.status === UserStatus.BLOCKED && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Reason Blocked</span>
                  <span className="text-sm text-gray-900">{user.reasonBlocked || '—'}</span>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Access Expires</span>
                <div className="flex items-center gap-2">
                  <span className={user.accessExpiredAt ? 'text-sm text-red-600' : 'text-sm text-gray-900'}>
                    {safeFormatDateTime(user.accessExpiredAt)}
                  </span>
                  <div className="ml-auto flex gap-1">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className="border border-gray-200 shrink-0"
                            onClick={() => setIsDatePickerOpen(true)}
                            size="xs"
                            variant="secondary"
                            disabled={isBusy}
                          >
                            <ClockIcon className="size-3" />
                            <span>Set Date</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="font-normal text-xs">
                          <p>Set or remove user access expiration date</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Email Verified</span>
                <div className="flex items-center gap-1">
                  {user.emailVerified ? (
                    <>
                      <BadgeCheckIcon className="size-5 fill-blue-600 text-white" />
                      <span className="text-sm text-blue-700">Verified</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="size-4 text-red-700" />
                      <span className="text-sm text-red-700">Unverified</span>
                    </>
                  )}
                  <div className="ml-auto flex gap-1">
                    {!user.emailVerified && (
                      <>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                className="border border-gray-200 shrink-0"
                                onClick={() => onResendVerification(userId)}
                                size="xs"
                                variant="secondary"
                                disabled={isBusy}
                              >
                                <MailIcon className="size-3" />
                                <span>Resend</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="font-normal text-xs">
                              <p>Send verification email to user</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                className="border border-gray-200 shrink-0"
                                onClick={() => onMarkAsVerified(userId)}
                                size="xs"
                                variant="secondary"
                                disabled={isBusy}
                              >
                                <BadgeCheckIcon className="size-3" />
                                <span>Mark Verified</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="font-normal text-xs">
                              <p>Manually mark email as verified</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Flags Section */}
          <div>
            <AdminUsersModalFeatureFlags
              effectiveFeatureFlags={user.effectiveFeatureFlags ?? []}
              featureFlags={user.featureFlags ?? []}
              isBusy={isBusy}
              onUpdateFeatureFlags={(flags) => onUpdateUserFeatureFlags(userId, flags)}
              userRole={user.profile?.role}
            />
          </div>

          {/* Access Controls Section (INTELLIGENCE and EXPERT users) */}
          <AdminUsersModalTags
            isBusy={isBusy}
            organizationBlacklist={user.organizationBlacklist}
            organizationWhitelist={user.organizationWhitelist}
            userId={userId}
            userRole={user.profile?.role}
            userTags={user.userTags}
          />

          {/* Identities Section */}
          <div className="group relative p-4 rounded-lg border border-gray-200 bg-white">
            <IconButton
              ariaLabel="Copy Identities"
              className="absolute top-2 right-2 border border-gray-200 hidden group-hover:flex"
              icon={isCopied('Identities') ? <CheckIcon /> : <CopyIcon />}
              onClick={() =>
                handleCopy(
                  'Identities',
                  user.identities?.map((identity) => ({
                    identifier: identity.identifier,
                    type: identity.type,
                    createdAt: safeFormatDateTime(identity.createdAt),
                  })),
                )
              }
              size="sm"
              variant="secondary"
            />
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Identities</h3>

              {user.identities && user.identities.length > 0 ? (
                user.identities.map((identity, index) => (
                  <div
                    key={identity.id}
                    className={
                      index < (user.identities?.length ?? 0) - 1
                        ? 'flex flex-col gap-2 pb-2 border-b border-gray-200'
                        : 'flex flex-col gap-2'
                    }
                  >
                    <div className="flex items-center gap-2">
                      {/* <span className="text-xs text-gray-900">{identity.type}</span> */}
                      <Badge
                        variant="secondary"
                        size="sm"
                      >
                        {identity.type}
                      </Badge>
                      <span className="text-xs text-gray-500">{safeFormatDateTime(identity.createdAt)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-500">No identities found</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Date Picker Dialog */}
      <Dialog
        open={isDatePickerOpen}
        onOpenChange={setIsDatePickerOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Access Expiration Date</DialogTitle>
            <DialogDescription>
              Choose when this user's access should expire. Leave empty to remove the expiration date.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center py-4">
            <Calendar
              autoFocus
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            />
          </div>

          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedDate(undefined);
                onSetAccessWindow(userId, null);
                setIsDatePickerOpen(false);
              }}
              disabled={isBusy}
            >
              Remove Expiration
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setIsDatePickerOpen(false);
                  setSelectedDate(user.accessExpiredAt ? new Date(user.accessExpiredAt) : undefined);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (selectedDate) {
                    onSetAccessWindow(userId, selectedDate.toISOString());
                  }
                  setIsDatePickerOpen(false);
                }}
                disabled={isBusy || !selectedDate}
              >
                <CalendarIcon className="size-3 mr-1" />
                Save Date
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Intelligence User Approval Confirmation Dialog */}
      <Dialog
        open={showIntelligenceConfirmation}
        onOpenChange={setShowIntelligenceConfirmation}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm approval of {userName}</DialogTitle>
            <DialogDescription>Are you sure you want to approve this Intelligence user?</DialogDescription>
          </DialogHeader>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Intelligence users have elevated access privileges. Please ensure this user has
              been properly vetted before approving.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowIntelligenceConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmIntelligenceApproval}
              disabled={isBusy}
            >
              Approve User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-32 w-full" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
      <Skeleton className="h-24 w-full" />
    </div>
  );
};

const AdminUsersModalHeaderTitle: React.FC<{ userId: string }> = (props) => {
  const { userId } = props;
  const { data: user } = useAdminUserDetailQuery(userId);

  const userName = user?.profile?.fullName || user?.email || '';

  return <span>User: {userName}</span>;
};

const AdminUsersModalFooter: React.FC<{ isBusy: boolean; userId: string }> = (props) => {
  const { isBusy, userId } = props;
  const { data: user } = useAdminUserDetailQuery(userId);
  const { mutateAsync: impersonateUser, isPending: isPendingImpersonate } = useAdminImpersonateUser();
  const currentUserQueryOptions = useCurrentUserQueryOptions();
  const queryClient = useQueryClient();

  const isTargetAdmin = user?.profile?.role === UserProfileRole.ADMIN;

  const handleImpersonate = useCallback(async () => {
    try {
      await impersonateUser(userId);
      const currentUser = await queryClient.fetchQuery(currentUserQueryOptions);
      const shouldStayOnAdmin = shouldRedirectAuthenticatedUserToAdmin({
        impersonation: currentUser.impersonation,
        role: currentUser.profile?.role,
      });

      if (shouldStayOnAdmin || !currentUser.impersonation) {
        errorToast({ description: 'Impersonation did not start. Please try again.' });
        return;
      }

      window.location.replace(routes.home.path);
    } catch (error) {
      console.error('Failed to impersonate user', error);
      errorToast({ description: 'Failed to impersonate user.' });
    }
  }, [currentUserQueryOptions, impersonateUser, queryClient, userId]);

  const isDisabled = isPendingImpersonate || isBusy || isTargetAdmin;

  const tooltipText = isTargetAdmin ? 'Cannot impersonate admin users' : 'Start a temporary session as this user';

  return (
    <div className="flex justify-end">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="border border-gray-200 shrink-0"
              onClick={handleImpersonate}
              variant="secondary"
              disabled={isDisabled}
            >
              {<UserIcon className="size-4" />}
              Impersonate
            </Button>
          </TooltipTrigger>
          <TooltipContent className="font-normal text-xs">
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export const AdminUsersModal: React.FC<AdminUsersModalProps> = (props) => {
  const {
    isBusy,
    isOpen,
    onApproveToggle,
    onBlockToggle,
    onClose,
    onMarkAsVerified,
    onResendVerification,
    onSetAccessWindow,
    onUpdateUserFeatureFlags,
    userId,
  } = props;

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      header={{
        title: userId ? (
          <Suspense fallback="User">
            <AdminUsersModalHeaderTitle userId={userId} />
          </Suspense>
        ) : (
          'User'
        ),
      }}
      width="840px"
      footer={{
        rightButton: userId ? (
          <Suspense fallback={null}>
            <AdminUsersModalFooter
              isBusy={isBusy}
              userId={userId}
            />
          </Suspense>
        ) : null,
      }}
    >
      {userId ? (
        <Suspense fallback={<LoadingSkeleton />}>
          <AdminUsersModalContent
            isBusy={isBusy}
            onApproveToggle={onApproveToggle}
            onBlockToggle={onBlockToggle}
            onMarkAsVerified={onMarkAsVerified}
            onResendVerification={onResendVerification}
            onSetAccessWindow={onSetAccessWindow}
            onUpdateUserFeatureFlags={onUpdateUserFeatureFlags}
            userId={userId}
          />
        </Suspense>
      ) : (
        <div className="text-sm text-gray-500">No user selected.</div>
      )}
    </Modal>
  );
};
